import React from "react";

/**
 * Mini renderer Markdown -> JSX adapté aux descriptions produits Bilan 2 Vie.
 * Supporte :
 *  - **gras**            -> <strong>
 *  - Titres avec ** au début et à la fin sur leur propre ligne -> <h3>
 *  - Lignes commençant par "- " -> liste à puces
 *  - Paragraphes séparés par lignes vides
 *
 * On reste léger volontairement (pas de dépendance npm).
 */

function renderInline(text) {
  // **gras** -> <strong>gras</strong>
  const parts = [];
  const regex = /\*\*([^*]+)\*\*/g;
  let lastIndex = 0;
  let m;
  let key = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) parts.push(text.slice(lastIndex, m.index));
    parts.push(<strong key={`b-${key++}`} className="text-white">{m[1]}</strong>);
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) parts.push(text.slice(lastIndex));
  return parts;
}

function isHeading(line) {
  // Ligne entièrement entourée de ** (et au moins 3 caractères entre)
  const trimmed = line.trim();
  return /^\*\*[^*]+\*\*$/.test(trimmed);
}

function headingText(line) {
  return line.trim().replace(/^\*\*/, "").replace(/\*\*$/, "");
}

/**
 * Convertit du Markdown en texte brut, pour les previews/cartes où on ne
 * peut pas se permettre de rendu HTML (line-clamp, meta-description, etc.).
 * Strip : **gras** *italique* __ _ # ## ### > [text](url) `code` listes - * 1.
 */
export function stripMarkdown(text) {
  if (!text) return "";
  return String(text)
    // images ![alt](url) → vide
    .replace(/!\[[^\]]*\]\([^)]*\)/g, "")
    // liens [text](url) → text
    .replace(/\[([^\]]+)\]\([^)]*\)/g, "$1")
    // code fences ```...```
    .replace(/```[\s\S]*?```/g, "")
    // inline code `...`
    .replace(/`([^`]+)`/g, "$1")
    // gras/italique
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/(^|[^*])\*([^*\n]+)\*/g, "$1$2")
    .replace(/(^|[^_])_([^_\n]+)_/g, "$1$2")
    // titres en début de ligne
    .replace(/^#{1,6}\s+/gm, "")
    // citations
    .replace(/^>\s?/gm, "")
    // puces de liste
    .replace(/^[\s]*[-*]\s+/gm, "")
    .replace(/^[\s]*\d+\.\s+/gm, "")
    // espaces multiples / sauts de ligne → un seul espace
    .replace(/\s+/g, " ")
    .trim();
}

export default function MarkdownLite({ content, className = "" }) {
  if (!content) return null;

  const lines = content.split(/\r?\n/);
  const blocks = [];
  let buf = []; // lignes courantes de paragraphe
  let listBuf = []; // items de liste

  const flushParagraph = () => {
    if (buf.length === 0) return;
    blocks.push({ type: "p", lines: buf });
    buf = [];
  };
  const flushList = () => {
    if (listBuf.length === 0) return;
    blocks.push({ type: "ul", items: listBuf });
    listBuf = [];
  };

  for (const raw of lines) {
    const line = raw;
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      flushList();
      continue;
    }
    if (isHeading(trimmed)) {
      flushParagraph();
      flushList();
      blocks.push({ type: "h3", text: headingText(trimmed) });
      continue;
    }
    if (/^[-*]\s+/.test(trimmed)) {
      flushParagraph();
      listBuf.push(trimmed.replace(/^[-*]\s+/, ""));
      continue;
    }
    // sinon, paragraphe
    flushList();
    buf.push(trimmed);
  }
  flushParagraph();
  flushList();

  return (
    <div className={`space-y-4 ${className}`}>
      {blocks.map((b, i) => {
        if (b.type === "h3") {
          return (
            <h3 key={i} className="font-serif text-lg text-white mt-4 mb-1 flex items-center gap-2">
              {renderInline(b.text)}
            </h3>
          );
        }
        if (b.type === "ul") {
          return (
            <ul key={i} className="list-disc list-outside pl-5 space-y-1.5 text-[#E2D5F8]">
              {b.items.map((it, j) => (
                <li key={j} className="leading-relaxed">{renderInline(it)}</li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="text-[#E2D5F8] leading-relaxed">
            {b.lines.map((l, j) => (
              <React.Fragment key={j}>
                {renderInline(l)}
                {j < b.lines.length - 1 ? <br /> : null}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
